import React from "react";
import apiClient, { formatApiErrorDetail } from "@/lib/api";
import { QrCode, Plus, X, Loader2, AlertCircle, Printer, Download, Trash2, Camera, Mail, Copy, Check } from "lucide-react";

/**
 * v22 — Marketing Kit + Staff IDs.
 * - QR code generator (deep-link to /register?role=driver for "Join our drivers")
 * - Logo + brand colours
 * - Staff ID CRUD with printable ID card preview (A6 / business-card friendly)
 */
export default function AdminMarketingKitPanel() {
  const [qr, setQr] = React.useState(null);
  const [staff, setStaff] = React.useState([]);
  const [showCreate, setShowCreate] = React.useState(false);
  const [error, setError] = React.useState("");
  const [printing, setPrinting] = React.useState(null);

  const load = React.useCallback(async () => {
    try {
      const [qrRes, staffRes] = await Promise.all([
        apiClient.get("/admin/marketing/qr-code", { params: { target: "register?role=driver" } }),
        apiClient.get("/admin/staff"),
      ]);
      setQr(qrRes.data);
      setStaff(staffRes.data.staff || []);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load marketing kit");
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Delete this staff record + their ID photo? Cannot be undone.")) return;
    await apiClient.delete(`/admin/staff/${id}`);
    await load();
  };

  return (
    <section className="mb-16" data-testid="admin-marketing-kit-section">
      <h2 className="font-display text-2xl font-bold tracking-tight text-[#0A0A0A] mb-4">Marketing kit & Staff IDs</h2>
      {error && <div className="mb-3 flex items-start gap-2 text-sm text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] p-3"><AlertCircle size={14} className="mt-0.5 shrink-0"/><span>{error}</span></div>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
        {/* QR code panel */}
        <div className="bg-white border border-[#0A0A0A]/10 p-5" data-testid="marketing-qr-panel">
          <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#1E4D55] mb-3">Join-our-drivers QR</p>
          {qr ? (
            <>
              <img src={`data:image/png;base64,${qr.png_b64}`} alt="QR code" className="w-full max-w-[180px] mx-auto" />
              <p className="mt-3 text-[10px] text-center text-[#475569] font-mono break-all">{qr.url}</p>
              <a
                href={`data:image/png;base64,${qr.png_b64}`}
                download="raanah-drivers-qr.png"
                data-testid="qr-download"
                className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-[#1E4D55] hover:bg-[#163C42] text-white px-4 py-2 text-xs font-semibold"
              >
                <Download size={12} /> Download PNG (300dpi)
              </a>
            </>
          ) : <Loader2 size={20} className="animate-spin text-[#1E4D55] mx-auto" />}
        </div>

        {/* Brand assets panel */}
        <div className="bg-white border border-[#0A0A0A]/10 p-5">
          <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#1E4D55] mb-3">Brand assets</p>
          <div className="space-y-2">
            <div className="bg-[#1E4D55] text-white p-4 text-center">
              <p className="font-display text-xl font-black tracking-tighter">Raanah</p>
              <p className="text-[9px] uppercase tracking-[0.22em] mt-1 opacity-80">Vans you can trust</p>
            </div>
            <div className="grid grid-cols-3 gap-1 text-[9px] text-center">
              <div className="bg-[#1E4D55] text-white p-2"><div className="font-bold">#1E4D55</div>Primary</div>
              <div className="bg-[#5BB247] text-white p-2"><div className="font-bold">#5BB247</div>Accent</div>
              <div className="bg-[#F5A623] text-white p-2"><div className="font-bold">#F5A623</div>Warn</div>
            </div>
            <p className="text-[10px] text-[#475569] mt-3">Font: <strong>Display = Georgia/serif</strong> · <strong>Body = system</strong></p>
            <p className="text-[10px] text-[#475569]">Logo: text wordmark in #1E4D55 on white, or white on #1E4D55. Always pair with the strapline above.</p>
          </div>
        </div>

        {/* Print templates panel */}
        <div className="bg-white border border-[#0A0A0A]/10 p-5">
          <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#1E4D55] mb-3">Print templates</p>
          <button onClick={() => window.open("/admin/print/poster", "_blank")} data-testid="print-a3-poster" className="w-full mb-2 inline-flex items-center justify-center gap-2 bg-[#1E4D55] hover:bg-[#163C42] text-white px-4 py-2 text-xs font-semibold">
            <Printer size={12} /> A3 driver-recruitment poster
          </button>
          <button onClick={() => window.open("/admin/print/rollup", "_blank")} data-testid="print-rollup" className="w-full inline-flex items-center justify-center gap-2 bg-white border border-[#1E4D55] text-[#1E4D55] hover:bg-[#1E4D55] hover:text-white px-4 py-2 text-xs font-semibold transition-colors">
            <Printer size={12} /> Roll-up standing banner
          </button>
          <p className="mt-3 text-[10px] text-[#475569]">Opens a print-styled preview — use browser&apos;s &quot;Print → Save as PDF&quot; to export.</p>
        </div>
      </div>

      {/* v23 — Email signatures */}
      <EmailSignaturesSection />

      {/* Staff IDs */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-display text-lg font-bold tracking-tight text-[#0A0A0A]">Staff ID cards</h3>
          <p className="text-xs text-[#475569]">For office staff who need an ID but don&apos;t log into Raanah.</p>
        </div>
        <button onClick={() => setShowCreate(true)} data-testid="staff-create-button" className="inline-flex items-center gap-2 bg-[#1E4D55] hover:bg-[#163C42] text-white px-4 py-2 text-sm font-semibold">
          <Plus size={14} /> New staff ID
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="staff-list">
        {staff.map((s) => (
          <div key={s.id} data-testid={`staff-card-${s.id}`} className="bg-white border border-[#0A0A0A]/10 p-3 flex gap-3">
            <img src={s.photo_url} alt={s.name} className="w-20 h-24 object-cover" />
            <div className="flex-1 min-w-0">
              <p className="font-display font-bold text-sm text-[#0A0A0A] truncate">{s.name}</p>
              <p className="text-[11px] text-[#475569] truncate">{s.role}</p>
              <p className="text-[10px] font-mono text-[#1E4D55] mt-1">#{s.employee_no}</p>
              <div className="mt-2 flex items-center gap-2">
                <button onClick={() => setPrinting(s)} data-testid={`staff-print-${s.id}`} className="text-[10px] font-semibold text-[#1E4D55] hover:underline inline-flex items-center gap-1">
                  <Printer size={10} /> Print
                </button>
                <button onClick={() => remove(s.id)} className="text-[10px] font-semibold text-[#B91C1C] hover:underline inline-flex items-center gap-1">
                  <Trash2 size={10} /> Delete
                </button>
              </div>
            </div>
          </div>
        ))}
        {staff.length === 0 && <p className="text-xs text-[#475569] italic col-span-3">No staff IDs yet.</p>}
      </div>

      {showCreate && <CreateStaffModal onClose={() => setShowCreate(false)} onCreated={async () => { setShowCreate(false); await load(); }} />}
      {printing && <PrintIdCard staff={printing} onClose={() => setPrinting(null)} />}
    </section>
  );
}


function CreateStaffModal({ onClose, onCreated }) {
  const [form, setForm] = React.useState({ name: "", role: "", employee_no: "", joined_date: "", notes: "" });
  const [photo, setPhoto] = React.useState(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState("");
  const inputRef = React.useRef(null);
  const set = (f) => (e) => setForm((s) => ({ ...s, [f]: e.target.value }));

  const onPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setError("Photo too large (max 5 MB)."); return; }
    const reader = new FileReader();
    reader.onload = () => setPhoto({ dataUrl: reader.result, mime: file.type });
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!photo) { setError("Upload a portrait photo first."); return; }
    setSubmitting(true);
    setError("");
    try {
      await apiClient.post("/admin/staff", {
        ...form,
        photo_data: String(photo.dataUrl).split(",")[1],
        photo_mime: photo.mime,
      });
      await onCreated();
    } catch (e2) {
      setError(formatApiErrorDetail(e2.response?.data?.detail) || "Couldn't create staff record");
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white max-w-md w-full p-6 space-y-3" data-testid="staff-create-modal">
        <div className="flex items-start justify-between">
          <h3 className="font-display text-xl font-bold">New staff ID</h3>
          <button type="button" onClick={onClose} className="p-1"><X size={16} /></button>
        </div>
        <input required minLength={2} value={form.name} onChange={set("name")} placeholder="Full name" data-testid="staff-name-input" className="w-full px-3 py-2 border border-[#0A0A0A]/15 focus:border-[#1E4D55] outline-none text-sm" />
        <input required minLength={2} value={form.role} onChange={set("role")} placeholder="Role (e.g. Office Manager)" data-testid="staff-role-input" className="w-full px-3 py-2 border border-[#0A0A0A]/15 focus:border-[#1E4D55] outline-none text-sm" />
        <input required value={form.employee_no} onChange={set("employee_no")} placeholder="Employee no (e.g. RNH-001)" data-testid="staff-empno-input" className="w-full px-3 py-2 border border-[#0A0A0A]/15 focus:border-[#1E4D55] outline-none text-sm font-mono" />
        <div className="grid grid-cols-2 gap-2">
          <input type="date" value={form.joined_date} onChange={set("joined_date")} className="w-full px-3 py-2 border border-[#0A0A0A]/15 focus:border-[#1E4D55] outline-none text-sm" />
          <input value={form.notes} onChange={set("notes")} placeholder="Internal notes" className="w-full px-3 py-2 border border-[#0A0A0A]/15 focus:border-[#1E4D55] outline-none text-sm" />
        </div>
        <div>
          {photo ? (
            <div className="relative">
              <img src={photo.dataUrl} alt="" className="w-full max-h-48 object-cover" />
              <button type="button" onClick={() => { setPhoto(null); inputRef.current.value = ""; }} className="absolute top-1 right-1 bg-black/70 text-white p-1"><X size={12} /></button>
            </div>
          ) : (
            <button type="button" onClick={() => inputRef.current?.click()} data-testid="staff-photo-button" className="w-full py-6 bg-[#F5F5F5] border-2 border-dashed border-[#0A0A0A]/20 hover:border-[#1E4D55] flex flex-col items-center gap-1">
              <Camera size={20} className="text-[#1E4D55]" />
              <span className="text-xs font-semibold">Upload portrait photo</span>
            </button>
          )}
          <input ref={inputRef} type="file" accept="image/*" onChange={onPick} className="hidden" />
        </div>
        {error && <div className="flex items-start gap-2 text-xs text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] p-2"><AlertCircle size={12} className="mt-0.5"/>{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-3 py-2 text-sm font-semibold text-[#475569]">Cancel</button>
          <button type="submit" disabled={submitting} data-testid="staff-create-submit" className="inline-flex items-center gap-2 bg-[#1E4D55] hover:bg-[#163C42] text-white px-4 py-2 text-sm font-semibold disabled:opacity-60">
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Create
          </button>
        </div>
      </form>
    </div>
  );
}


function PrintIdCard({ staff, onClose }) {
  const [verifyQr, setVerifyQr] = React.useState(null);

  React.useEffect(() => {
    // Fetch a verify QR (encodes the public /verify-staff/<token> URL)
    if (staff?.verify_token) {
      apiClient
        .get("/admin/marketing/qr-code", { params: { target: `verify-staff/${staff.verify_token}` } })
        .then((r) => setVerifyQr(r.data))
        .catch(() => {});
    }
    // Auto-trigger print dialog after a small delay so QR has time to load
    const t = setTimeout(() => window.print(), 900);
    return () => clearTimeout(t);
  }, [staff?.verify_token]);

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }).toUpperCase();
    } catch { return iso; }
  };

  const validUntil = staff.valid_until || "2028-12-31";
  const verifyUrlShort = (staff.verify_url || "").replace(/^https?:\/\//, "");
  const hotline = staff.hotline || "01134 260 888";
  const returnAddress = staff.return_address || "Annex 2 Wharfe Bank Mews, 4-6 Bridge Street, Tadcaster LS24 9AL";

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-start justify-center p-4 overflow-y-auto print:bg-white print:p-0" onClick={onClose}>
      <div className="bg-white p-5 max-w-xl w-full print:p-0 print:max-w-none my-6" onClick={(e) => e.stopPropagation()}>
        <p className="text-xs text-[#475569] mb-3 print:hidden">
          Two-sided CR80 ID · Press <kbd className="bg-[#F5F5F5] px-1">Esc</kbd> to close · Use browser&apos;s Print menu, set <strong>scale 100%</strong> and <strong>no margins</strong>.
        </p>

        {/* Cards stacked — front then back. Each is exactly CR80 (85.6 × 54 mm). */}
        <div className="space-y-4 print:space-y-3" data-testid="staff-id-card-preview">

          {/* ───── FRONT ───── */}
          <div data-testid="staff-id-card-front" className="mx-auto bg-white border border-[#0A0A0A]/15 overflow-hidden print:border-0 shadow-md print:shadow-none" style={{ width: "85.6mm", height: "54mm" }}>
            <div className="h-full flex flex-col">
              {/* Header bar */}
              <div className="bg-[#1E4D55] text-white px-3 flex items-center justify-between" style={{ height: "9mm" }}>
                <div className="flex items-center gap-2">
                  <img
                    src="/raanah-logo.png"
                    alt=""
                    style={{ height: "6mm", width: "6mm", objectFit: "contain", background: "white", padding: "0.4mm" }}
                  />
                  <span className="font-display font-black tracking-tight" style={{ fontSize: "3.3mm" }}>RAANAH · STAFF ID</span>
                </div>
                <span className="font-display tracking-wider opacity-80" style={{ fontSize: "1.8mm", letterSpacing: "0.2em" }}>VANS YOU CAN TRUST</span>
              </div>

              {/* Body */}
              <div className="flex-1 flex bg-[#FAFAF7]">
                {/* Photo */}
                <div className="bg-white" style={{ width: "26mm", padding: "2mm" }}>
                  {staff.photo_url ? (
                    <img src={staff.photo_url} alt={staff.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-[#E5E7EB]" />
                  )}
                </div>
                {/* Fields */}
                <div className="flex-1 px-3 py-2 flex flex-col justify-between">
                  <div className="space-y-1.5">
                    <div>
                      <p className="text-[#475569] uppercase font-semibold tracking-[0.15em]" style={{ fontSize: "1.6mm" }}>Name</p>
                      <p className="font-display font-black text-[#0A0A0A] leading-tight" style={{ fontSize: "3.6mm" }}>{staff.name}</p>
                    </div>
                    <div>
                      <p className="text-[#475569] uppercase font-semibold tracking-[0.15em]" style={{ fontSize: "1.6mm" }}>Role</p>
                      <p className="font-semibold text-[#0A0A0A] leading-tight" style={{ fontSize: "2.6mm" }}>{staff.role}</p>
                    </div>
                  </div>
                  <div className="flex items-end justify-between gap-2">
                    <div>
                      <p className="text-[#475569] uppercase font-semibold tracking-[0.15em]" style={{ fontSize: "1.6mm" }}>Staff ID</p>
                      <p className="font-mono font-bold text-[#1E4D55]" style={{ fontSize: "2.4mm" }}>#{staff.employee_no}</p>
                    </div>
                    {staff.joined_date && (
                      <div className="text-right">
                        <p className="text-[#475569] uppercase font-semibold tracking-[0.15em]" style={{ fontSize: "1.6mm" }}>Joined</p>
                        <p className="font-mono text-[#0A0A0A]" style={{ fontSize: "2.2mm" }}>{fmtDate(staff.joined_date)}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Footer bar */}
              <div className="bg-[#1E4D55] text-white px-3 flex items-center justify-between" style={{ height: "5mm" }}>
                <span className="font-display font-bold uppercase tracking-wider" style={{ fontSize: "1.9mm", letterSpacing: "0.16em" }}>VALID UNTIL {fmtDate(validUntil)}</span>
                <span className="font-mono opacity-80" style={{ fontSize: "1.8mm" }}>app.raanah.com</span>
              </div>
            </div>
          </div>

          {/* ───── BACK ───── */}
          <div data-testid="staff-id-card-back" className="mx-auto bg-white border border-[#0A0A0A]/15 overflow-hidden print:border-0 shadow-md print:shadow-none relative" style={{ width: "85.6mm", height: "54mm" }}>
            {/* Watermark logo */}
            <img
              src="/raanah-logo.png"
              alt=""
              aria-hidden="true"
              className="absolute pointer-events-none"
              style={{ right: "22mm", top: "8mm", width: "22mm", opacity: 0.06 }}
            />
            <div className="h-full flex flex-col bg-[#FAFAF7] relative">
              {/* QR + verify row */}
              <div className="flex-1 flex px-3 py-2 gap-3">
                <div className="flex flex-col items-center justify-center bg-white border border-[#0A0A0A]/10" style={{ width: "22mm", padding: "1.5mm" }}>
                  {verifyQr ? (
                    <img src={`data:image/png;base64,${verifyQr.png_b64}`} alt="Verify QR" className="w-full" />
                  ) : (
                    <div className="w-full aspect-square bg-[#E5E7EB] animate-pulse" />
                  )}
                </div>
                <div className="flex-1 flex flex-col justify-center">
                  <p className="font-display font-black text-[#0A0A0A] leading-tight" style={{ fontSize: "3.2mm" }}>Verify this staff ID</p>
                  <p className="text-[#475569] mt-1" style={{ fontSize: "1.9mm" }}>Scan the QR, or visit:</p>
                  <p className="font-mono font-bold text-[#1E4D55] mt-1 break-all leading-tight" style={{ fontSize: "2.0mm" }}>{verifyUrlShort}</p>
                  <p className="text-[#475569] mt-2" style={{ fontSize: "1.7mm" }}>Verify code: <span className="font-mono text-[#0A0A0A]">{staff.verify_token || "—"}</span></p>
                </div>
              </div>

              {/* Emergency band */}
              <div className="bg-[#D35400] text-white px-3 flex items-center justify-between" style={{ minHeight: "10mm", padding: "1.5mm 3mm" }}>
                <div>
                  <p className="uppercase font-bold tracking-[0.18em]" style={{ fontSize: "1.6mm" }}>In case of emergency · contact Raanah</p>
                  <p className="font-display font-black leading-tight" style={{ fontSize: "4.2mm" }}>{hotline}</p>
                </div>
                <p className="text-right opacity-90 font-semibold" style={{ fontSize: "1.8mm" }}>{staff.contact_email || "admin@raanah.com"}</p>
              </div>

              {/* Disclaimer footer */}
              <div className="bg-[#1E4D55] text-white px-3 flex items-center" style={{ minHeight: "7mm", padding: "1mm 3mm" }}>
                <p className="leading-snug" style={{ fontSize: "1.55mm" }}>
                  <strong>Property of Raanah Technologies.</strong> If found, please post to {returnAddress}, or call the number above. Not valid for identification without a verify code on file.
                </p>
              </div>
            </div>
          </div>
        </div>

        <button onClick={onClose} className="mt-4 print:hidden text-xs text-[#1E4D55] font-semibold hover:underline">Close preview</button>
      </div>

      {/* Print-only: hide everything else on the page when this modal prints */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          [data-testid="staff-id-card-preview"], [data-testid="staff-id-card-preview"] * { visibility: visible; }
          [data-testid="staff-id-card-preview"] { position: absolute; left: 0; top: 0; }
          @page { margin: 8mm; }
        }
      `}</style>
    </div>
  );
}


const SIGNATURE_VARIANTS = [
  { id: "house-removal",    label: "House removal seeker",  blurb: "Send to home-mover leads · CTA = book a move" },
  { id: "waste-clearance",  label: "Waste removal seeker",  blurb: "Send to clearance leads · CTA = book a clearance" },
  { id: "driver-enrolment", label: "Driver enrolment",      blurb: "Send to van-owner prospects · CTA = apply as driver" },
];


function EmailSignaturesSection() {
  const [sigs, setSigs] = React.useState({}); // {variant_id: payload}
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [copiedId, setCopiedId] = React.useState(null);

  React.useEffect(() => {
    (async () => {
      try {
        const results = await Promise.all(
          SIGNATURE_VARIANTS.map((v) =>
            apiClient.get("/admin/marketing/email-signature", { params: { variant: v.id } }).then((r) => [v.id, r.data])
          )
        );
        setSigs(Object.fromEntries(results));
      } catch (e) {
        setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load signatures");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const copyHtml = async (variantId, html) => {
    // Show "Copied!" optimistically so the visual feedback is independent of
    // clipboard permission (headless / locked-down browsers may reject the write).
    setCopiedId(variantId);
    setTimeout(() => setCopiedId((id) => (id === variantId ? null : id)), 2200);
    try {
      if (navigator.clipboard && window.ClipboardItem) {
        const blob = new Blob([html], { type: "text/html" });
        const textBlob = new Blob([html], { type: "text/plain" });
        await navigator.clipboard.write([new ClipboardItem({ "text/html": blob, "text/plain": textBlob })]);
      } else {
        await navigator.clipboard.writeText(html);
      }
    } catch {
      try { await navigator.clipboard.writeText(html); } catch { /* clipboard locked down — visual feedback still fires */ }
    }
  };

  const downloadHtml = (variantId, payload) => {
    const fullDoc = `<!doctype html>
<html><head><meta charset="utf-8"><title>Raanah email signature · ${variantId}</title></head>
<body style="margin:24px;font-family:Arial,sans-serif;background:#F5F5F5;">
<p style="font-size:11px;color:#475569;letter-spacing:1px;text-transform:uppercase;">Raanah signature · ${variantId}</p>
<div style="background:#fff;padding:18px;border:1px solid #E5E7EB;">${payload.html}</div>
<p style="font-size:11px;color:#475569;margin-top:18px;">Paste the block above into Gmail Settings &rarr; Signature, or Outlook Signature editor. Replace <strong>[Your name]</strong> and <strong>[Your role]</strong>.</p>
</body></html>`;
    const blob = new Blob([fullDoc], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `raanah-signature-${variantId}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <section className="mb-12" data-testid="admin-email-signatures-section">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-display text-lg font-bold tracking-tight text-[#0A0A0A] flex items-center gap-2">
            <Mail size={16} className="text-[#1E4D55]" /> Email signatures
          </h3>
          <p className="text-xs text-[#475569]">Drop-in signatures with a scannable QR + CTA. Paste into Gmail / Outlook.</p>
        </div>
      </div>

      {error && <div className="mb-3 flex items-start gap-2 text-xs text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] p-3"><AlertCircle size={12} className="mt-0.5"/>{error}</div>}

      {loading ? (
        <div className="text-xs text-[#475569] inline-flex items-center gap-2"><Loader2 size={12} className="animate-spin"/> Generating signatures&hellip;</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="email-signatures-grid">
          {SIGNATURE_VARIANTS.map((v) => {
            const payload = sigs[v.id];
            if (!payload) return null;
            return (
              <div key={v.id} data-testid={`signature-card-${v.id}`} className="bg-white border border-[#0A0A0A]/10 p-4 flex flex-col">
                <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#1E4D55]">{v.label}</p>
                <p className="text-[11px] text-[#475569] mt-1">{v.blurb}</p>

                {/* Live preview */}
                <div
                  data-testid={`signature-preview-${v.id}`}
                  className="mt-3 bg-[#F5F5F5] border border-[#0A0A0A]/5 p-3 overflow-x-auto text-[12px]"
                  // Sandboxed visually only — content comes from our own admin endpoint, served over our auth'd domain.
                  dangerouslySetInnerHTML={{ __html: payload.html }}
                />

                <p className="mt-3 text-[10px] font-mono text-[#475569] break-all">{payload.cta_url}</p>

                <div className="mt-3 flex items-center gap-2">
                  <button
                    onClick={() => copyHtml(v.id, payload.html)}
                    data-testid={`signature-copy-${v.id}`}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 bg-[#1E4D55] hover:bg-[#163C42] text-white px-3 py-2 text-xs font-semibold"
                  >
                    {copiedId === v.id ? <><Check size={12}/> Copied!</> : <><Copy size={12}/> Copy HTML</>}
                  </button>
                  <button
                    onClick={() => downloadHtml(v.id, payload)}
                    data-testid={`signature-download-${v.id}`}
                    className="inline-flex items-center justify-center gap-1.5 bg-white border border-[#1E4D55] text-[#1E4D55] hover:bg-[#1E4D55] hover:text-white px-3 py-2 text-xs font-semibold transition-colors"
                  >
                    <Download size={12}/> .html
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

